/**
 * Report ThumbmarkJS hashes and browser locale to /v1/device-identify.
 * Refresh changed locale values on page load or resume, and unchanged values daily.
 */

import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';
import apiClient from './api-client.js';
import { getApiToken } from './tokenUtils.js';
import { getJwtToken } from './jwtTokenManager.js';
import { isNetworkErrorMessage } from './network-errors.js';
import { safeStorageAvailable, safeStorageGet, safeStorageSet, SESSION_STORAGE } from './safe-storage.js';

const SESSION_GATE_KEY_PREFIX = 'ip/device-identified:';
const CAPTURE_INTERVAL_MS = 24 * 60 * 60 * 1000;
const RATE_LIMITED_BACKOFF_MS = 60 * 60 * 1000;
const FAILURE_BACKOFF_MS = 15 * 60 * 1000;
const pendingByUser = new Map();
let thumbmarks;

/** Read the account ID for capture cadence. Server authentication is authoritative. */
function currentUserIdFromToken(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return 'anon';
        }
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return String(payload.uid ?? 'anon');
    } catch (_) {
        return 'anon';
    }
}

/** Exclude volatile components from the stable device ID. */
function buildStableThumbmark() {
    return new Thumbmark({
        exclude: ['canvas', 'audio', 'permissions'],
        logging: false,
    });
}

function buildFullThumbmark() {
    return new Thumbmark({ logging: false });
}

/** Omit unavailable client locale values so the server can distinguish missing data. */
function deviceLocaleFields() {
    const fields = {};

    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timeZone) {
            fields.tz = String(timeZone);
        }
    } catch (_) {
        // Intl is absent or the resolver threw. Nothing to report.
    }

    const languages = Array.isArray(navigator.languages) ? navigator.languages : [];
    const locales = languages.filter((lang) => typeof lang === 'string' && lang !== '');
    if (locales.length > 0) {
        // The column holds 128 chars; a handful of tags is the whole signal.
        fields.os_locales = locales.slice(0, 8).join(',');
    }

    return fields;
}

function currentIdentity(token) {
    const tokenUserId = currentUserIdFromToken(token);
    const renderedUserId = String(document.querySelector('meta[name="user-id"]')?.content || '');
    const stored = getJwtToken();
    return tokenUserId !== 'anon' && tokenUserId === renderedUserId
        && String(stored?.account?.userId || '') === tokenUserId
        && stored?.auth?.token === token;
}

function captureDue(gateKey, fields) {
    try {
        const previous = JSON.parse(safeStorageGet(gateKey, SESSION_STORAGE) || 'null');
        // A backoff wins over a locale change, so a refused capture cannot repeat on each page view.
        const backoffLeft = previous?.retry_at - Date.now();
        if (backoffLeft > 0 && backoffLeft <= CAPTURE_INTERVAL_MS) {
            return false;
        }
        return previous?.tz !== (fields.tz || null)
            || previous?.os_locales !== (fields.os_locales || null)
            || !Number.isFinite(previous?.at)
            || Date.now() - previous.at >= CAPTURE_INTERVAL_MS
            || Date.now() < previous.at;
    } catch (_) {
        return true;
    }
}

/** Honour a delay-seconds Retry-After, capped at one capture interval. */
function backoffAfter(response) {
    if (response?.status !== 429) {
        return FAILURE_BACKOFF_MS;
    }
    const retryAfter = String(response.headers?.get?.('Retry-After') ?? '').trim();
    if (!/^\d+$/.test(retryAfter)) {
        return RATE_LIMITED_BACKOFF_MS;
    }
    return Math.min(Math.max(Number(retryAfter) * 1000, 1000), CAPTURE_INTERVAL_MS);
}

/** Keep the last successful values so the capture stays due once the backoff ends. */
function armBackoff(gateKey, delayMs) {
    let previous = null;
    try {
        previous = JSON.parse(safeStorageGet(gateKey, SESSION_STORAGE) || 'null');
    } catch {
        // A corrupt entry carries no success to keep.
    }
    safeStorageSet(gateKey, JSON.stringify({
        tz: previous?.tz ?? null,
        os_locales: previous?.os_locales ?? null,
        at: previous?.at ?? null,
        retry_at: Date.now() + delayMs,
    }), SESSION_STORAGE);
}

function browserHashes() {
    if (!thumbmarks) {
        thumbmarks = Promise.all([buildStableThumbmark().get(), buildFullThumbmark().get()])
            .then(([stable, full]) => ({ stable: stable.thumbmark, full: full.thumbmark }))
            .catch(error => {
                thumbmarks = null;
                throw error;
            });
    }
    return thumbmarks;
}

async function captureDevice(token, userId, gateKey) {
    let posted = false;
    try {
        if (!currentIdentity(token)) return;
        let fields = deviceLocaleFields();
        if (!captureDue(gateKey, fields)) return;
        const hashes = await browserHashes();
        if (!currentIdentity(token)) return;
        fields = deviceLocaleFields();
        if (!captureDue(gateKey, fields)) return;

        const body = new URLSearchParams({ ...hashes, ...fields, expected_user_id: userId });

        posted = true;
        const response = await apiClient.request('/device-identify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        });

        if (!currentIdentity(token)) return;
        if (response.ok) {
            safeStorageSet(gateKey, JSON.stringify({
                tz: fields.tz || null,
                os_locales: fields.os_locales || null,
                at: Date.now(),
            }), SESSION_STORAGE);
        } else {
            armBackoff(gateKey, backoffAfter(response));
        }
    } catch (err) {
        // Network failures leave the capture due once a short backoff ends.
        if (err instanceof TypeError && isNetworkErrorMessage(err.message)) {
            if (posted && currentIdentity(token)) armBackoff(gateKey, FAILURE_BACKOFF_MS);
            return;
        }
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
    }
}

export async function identifyDevice() {
    try {
        const token = await getApiToken();
        if (!token || !safeStorageAvailable(SESSION_STORAGE) || !currentIdentity(token)) return;
        const userId = currentUserIdFromToken(token);
        const gateKey = SESSION_GATE_KEY_PREFIX + userId;
        const previous = pendingByUser.get(userId) || Promise.resolve();
        const pending = previous.catch(() => {}).then(() => captureDevice(token, userId, gateKey));
        pendingByUser.set(userId, pending);
        await pending;
        if (pendingByUser.get(userId) === pending) pendingByUser.delete(userId);
    } catch (error) {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(error);
        }
    }
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', identifyDevice);
    } else {
        identifyDevice();
    }
}
